import { Fragment, useRef } from 'react';

import { CellLayout, EmptyState, InteractiveTable, LoadingTableRows, MoreButton, Table } from '@ingrid/luterkort-react';

import { CarrierTransportProduct } from '~src/components/CarrierTransportProduct/CarrierTransportProduct';
import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { TableAddressSecond } from '~src/components/TableAddress/TableAddress';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';

import { PendingShipmentListItem } from '../queries';
import { DetailsDrawer } from './details-drawer';
import { Menu } from './menu';

interface Props {
  loadRowCount: number;
  pendingShipments: PendingShipmentListItem[];
  isEmpty: boolean;
  loading: boolean;
}

export function ListTable(props: Props) {
  const { isEmpty, loadRowCount, loading, pendingShipments } = props;

  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const selectedPendingShipment = pendingShipments.find(ps => ps.id === highlightedKey);
  const tableRef = useRef<HTMLTableElement>(null);

  if (isEmpty) {
    return <EmptyState title={t('pendingShipment.list.noMatchingContacts')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={tableRef}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '20%' }}>{t('common.pendingShipmentId')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '20%' }}>{t('common.shipFrom')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '20%' }}>{t('common.shipTo')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '25%' }}>{t('common.carrierProduct')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>ETA</Table.HeadCell>
            <Table.HeadCell css={{ width: '25%' }}>{t('common.expiresAt')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {pendingShipments.map(ps => (
            <InteractiveTable.Row key={ps.id} value={ps.id!}>
              <Table.Cell>
                <Ellipsis>{ps.lookup_id ?? '-'}</Ellipsis>
                {ps.payload.external_id && <TableAddressSecond>{ps.payload.external_id}</TableAddressSecond>}
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{ps.payload.address_from?.name ?? '-'}</Ellipsis>
                <TableAddressSecond>{ps.payload.address_from?.city ?? '-'}</TableAddressSecond>
              </Table.Cell>
              <Table.Cell>
                <Ellipsis>{ps.payload.address_to?.name ?? '-'}</Ellipsis>
                <TableAddressSecond>{ps.payload.address_to?.city ?? '-'}</TableAddressSecond>
              </Table.Cell>
              <Table.Cell>
                <CellLayout.Row>
                  <CarrierTransportProduct
                    queryOptions={{ onlyConfigured: false }}
                    methodId={ps.payload.shipping_method}
                    width={32}
                  />
                </CellLayout.Row>
              </Table.Cell>
              <Table.Cell>
                {ps.payload.shipping_date ? <LocalizedDate dateTime={ps.payload.shipping_date} /> : '-'}
              </Table.Cell>
              <Table.Cell>
                <LocalizedDate dateTime={ps.expires_at!} time />
              </Table.Cell>
              <Table.Cell>
                <Menu pendingShipment={ps} trigger={<MoreButton size="sm" />} />
              </Table.Cell>
            </InteractiveTable.Row>
          ))}
          {loading && <LoadingTableRows tableRef={tableRef} rowCount={loadRowCount || 0} />}
        </tbody>
      </InteractiveTable.Table>

      <DetailsDrawer pendingShipment={selectedPendingShipment} />
    </Fragment>
  );
}
